import { useAllCounterpartiesFilter } from '@src/hooks/filters/useAllCounterpartiesFilter';
import { useMarketAccountsFilter } from '@src/hooks/filters/useMarketAccountsFilter';
import {
  IconName,
  isExcludedByCurrencyOrBasePairFilters,
  useBasePairFilter,
  useCurrenciesFilter,
  useCurrencyOfSymbolFilter,
  useGenericFilter,
  useSecuritiesContext,
  useSymbolsFilter,
  type BlotterTableFilter,
  type ClientSideFilterRule,
  type FilterableProperty,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { CounterpartyMonitoringProductType, type CounterpartyMonitoringRow } from './counterpartyMonitoringRows';

export interface CounterpartyMonitoringFilter {
  MarginCurrencies?: string[];
  Counterparties?: string[];
  MarketAccounts?: string[];
  Symbols?: string[];
  Currencies?: string[];
  BasePairs?: string[];
  ProductTypes?: string[];
}

const COUNTERPARTY_MONITORING_FILTER_KEYS = [
  'MarginCurrencies',
  'Counterparties',
  'MarketAccounts',
  'Symbols',
  'Currencies',
  'BasePairs',
  'ProductTypes',
] as const satisfies readonly (keyof CounterpartyMonitoringFilter)[];

export const COUNTERPARTY_MONITORING_FILTER_RULES: ClientSideFilterRule<CounterpartyMonitoringFilter>[] = [
  {
    type: 'include',
    filterKey: 'MarginCurrencies',
    dataKey: 'MarginCurrency',
  },
  {
    type: 'include',
    filterKey: 'Counterparties',
    dataKey: 'Counterparty',
  },
  {
    type: 'include',
    filterKey: 'MarketAccounts',
    dataKey: 'MarketAccount',
  },
  {
    type: 'include',
    filterKey: 'Symbols',
    dataKey: 'Symbol',
  },
  {
    type: 'include',
    filterKey: 'ProductTypes',
    dataKey: 'productType',
  },
];

export function toCounterpartyMonitoringFilter(filter: BlotterTableFilter | undefined): CounterpartyMonitoringFilter {
  if (!filter) {
    return {};
  }

  return pick(filter, COUNTERPARTY_MONITORING_FILTER_KEYS);
}

export function counterpartyMonitoringClientSideFilter(
  row: { data: CounterpartyMonitoringRow | null | undefined },
  genericClientSideFilter: ((row: { data: CounterpartyMonitoringRow | null | undefined }) => boolean) | undefined
) {
  // AG Grid can evaluate external filters for row-group nodes, which do not carry leaf row data.
  // Keep group nodes visible and let their filtered children decide whether the group remains.
  if (row.data == null) {
    return true;
  }

  return genericClientSideFilter?.(row) ?? true;
}

export interface UseCounterpartyMonitoringFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export function useCounterpartyMonitoringFilterableProperties() {
  const collateralCurrencyFilter = useCurrenciesFilter();
  const counterpartiesFilter = useAllCounterpartiesFilter({ label: 'Counterparty' });
  const marketAccountsFilter = useMarketAccountsFilter({ label: 'Market Account' });
  const symbolsFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();

  return useMemo(
    () =>
      compact<FilterableProperty<keyof CounterpartyMonitoringFilter>>([
        {
          ...collateralCurrencyFilter,
          key: 'MarginCurrencies',
          field: 'MarginCurrency',
          label: 'Collateral Asset',
        },
        counterpartiesFilter,
        marketAccountsFilter,
        {
          ...symbolsFilter,
          key: 'Symbols',
          field: 'Symbol',
          label: 'Symbol',
        },
        currencyOfSymbolFilter,
        basePairFilter,
        {
          key: 'ProductTypes',
          label: 'Product Type',
          field: 'productType',
          icon: IconName.Collection,
          control: 'multi-select',
          options: Object.values(CounterpartyMonitoringProductType),
          getOptionLabel: (productType: string) => productType,
        },
      ]),
    [
      collateralCurrencyFilter,
      counterpartiesFilter,
      marketAccountsFilter,
      symbolsFilter,
      currencyOfSymbolFilter,
      basePairFilter,
    ]
  );
}

export function useCounterpartyMonitoringFilter({ persistedBlotterTable }: UseCounterpartyMonitoringFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const { securitiesBySymbol } = useSecuritiesContext();
  const filterableProperties = useCounterpartyMonitoringFilterableProperties();

  const genericFilter = useGenericFilter<CounterpartyMonitoringFilter>(filterableProperties, {
    includeDateRange: false,
    initialFilter: toCounterpartyMonitoringFilter(persistedBlotterTable.initialState?.filter),
    onFilterChanged: filter => saveFilter(toCounterpartyMonitoringFilter(filter)),
    clientSideFilterRules: COUNTERPARTY_MONITORING_FILTER_RULES,
  });
  const { clientSideFilter: genericClientSideFilter, filter } = genericFilter;

  const clientSideFilter = useCallback(
    (row: { data: CounterpartyMonitoringRow | null | undefined }) => {
      if (!counterpartyMonitoringClientSideFilter(row, genericClientSideFilter)) {
        return false;
      }
      if (row.data == null) {
        return true;
      }
      if (
        isExcludedByCurrencyOrBasePairFilters(row.data.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)
      ) {
        return false;
      }
      return true;
    },
    [genericClientSideFilter, securitiesBySymbol, filter.Currencies, filter.BasePairs]
  );

  return {
    ...genericFilter,
    clientSideFilter,
  };
}
